"use client";

import { useEffect, useId, useMemo, useRef, useState } from "react";
import { AnimatePresence, motion, useReducedMotion } from "framer-motion";
import { HomeTestimonialCard } from "@/components/home/cards/HomeTestimonialCard";
import type { Testimonial } from "@/lib/api-types";
import {
  getTestimonialWindow,
  HOME_TESTIMONIALS_VISIBLE_COUNT,
} from "@/lib/testimonials-map";

const AUTO_INTERVAL_MS = 8_000;

type HomeTestimonialsRotatorProps = {
  testimonials: Testimonial[];
};

export function HomeTestimonialsRotator({
  testimonials,
}: HomeTestimonialsRotatorProps) {
  const reduced = useReducedMotion();
  const liveRegionId = useId();
  const containerRef = useRef<HTMLDivElement>(null);
  const total = testimonials.length;

  const [offset, setOffset] = useState(0);
  const [isHovered, setIsHovered] = useState(false);
  const [isFocusedWithin, setIsFocusedWithin] = useState(false);
  const [progressKey, setProgressKey] = useState(0);

  const visible = useMemo(
    () => getTestimonialWindow(testimonials, offset),
    [testimonials, offset]
  );

  const autoEnabled =
    !reduced &&
    !isHovered &&
    !isFocusedWithin &&
    total > HOME_TESTIMONIALS_VISIBLE_COUNT;

  useEffect(() => {
    if (!autoEnabled) return;

    const timer = window.setInterval(() => {
      setOffset((current) => current + 1);
      setProgressKey((k) => k + 1);
    }, AUTO_INTERVAL_MS);

    return () => window.clearInterval(timer);
  }, [autoEnabled, progressKey]);

  const handleFocusIn = () => setIsFocusedWithin(true);
  const handleFocusOut = (e: React.FocusEvent) => {
    if (
      containerRef.current &&
      !containerRef.current.contains(e.relatedTarget as Node)
    ) {
      setIsFocusedWithin(false);
    }
  };

  if (visible.length === 0) return null;

  return (
    <div
      ref={containerRef}
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
      onFocusCapture={handleFocusIn}
      onBlurCapture={handleFocusOut}
    >
      <div
        id={liveRegionId}
        className="sr-only"
        aria-live="polite"
        aria-atomic="true"
      >
        {total > HOME_TESTIMONIALS_VISIBLE_COUNT
          ? `Showing ${visible.length} of ${total} testimonials`
          : `Showing ${visible.length} testimonial${visible.length === 1 ? "" : "s"}`}
      </div>

      <AnimatePresence mode="wait">
        <motion.div
          key={offset}
          initial={reduced ? false : { opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={reduced ? undefined : { opacity: 0 }}
          transition={{ duration: reduced ? 0 : 0.35 }}
          className="space-y-4"
        >
          {visible.map((testimonial) => (
            <HomeTestimonialCard
              key={testimonial.id}
              quote={testimonial.quote}
              name={testimonial.name}
              role={testimonial.role}
              image={testimonial.image}
            />
          ))}
        </motion.div>
      </AnimatePresence>
    </div>
  );
}
